import { RouteLocationNormalized, Router } from 'vue-router'
import { UserService, EinvoiceService, AppService, ModuleNameType } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'

type MiddlewareFunction = (
  to: RouteLocationNormalized
) => Promise<void | { name: string } | boolean>

const authMiddleware: MiddlewareFunction = async (to) => {
  const store = useAppStore()
  const tenant = store.tenant
  const user = store.user

  const canAccessAdmin = UserService.allows('nav_access_admin')
  const canAccessSales = UserService.canAccessSales()

  const isTenantRequiredRoute = to.meta?.auth === 'tenant' || to.meta?.auth === 'user'
  const isUserRequiredRoute = to.meta?.auth === 'user'

  const isTenantLoginRoute = to.name === 'tenantLogin'
  const isUserLoginRoute = to.name === 'userLogin'

  // Tenant No, User No
  if (!tenant) {
    if (isTenantRequiredRoute && !isTenantLoginRoute) return { name: 'tenantLogin' }
    return undefined
  }

  // Tenant: Yes
  if (isUserLoginRoute) return undefined
  if (!isTenantRequiredRoute && !isUserRequiredRoute) return { name: 'userLogin' }

  // Tenant Yes, User No
  if (!user) {
    if (isUserRequiredRoute && !isUserLoginRoute) return { name: 'userLogin' }
    return undefined
  }

  // Tenant Yes, User Yes
  if(isUserRequiredRoute && !canAccessSales) {
    if(canAccessAdmin) {
      AppService.goToAdministration()
      return false
    } else {
      return { name: 'userLogin' }
    }
  }
}

const gettingStartedMiddleware: MiddlewareFunction = async (to) => {
  const store = useAppStore()
  const tenant = store.tenant

  if (!tenant) return undefined

  const isGettingStartedRoute = to.name === 'gettingStarted'
  const isGettingStartedCompleted = tenant.getstarted_status === 'completed'

  if (!isGettingStartedRoute && !isGettingStartedCompleted) {
    return { name: 'gettingStarted' }
  }

  if (isGettingStartedRoute && isGettingStartedCompleted) {
    return { name: 'userLogin' }
  }
}

const kitchenRouteMiddleware: MiddlewareFunction = async (to) => {
  const store = useAppStore()
  const tenant = store.tenant
  const user = store.user

  const isKDSTerminal = store.terminal?.isKitchenDisplaySystem()
  const isKitchenRoute = ['kitchen', 'kitchenOpenTickets', 'kitchenDoneTickets'].includes(to.name as string)

  if (tenant && isKDSTerminal && !isKitchenRoute) {
    return { name: 'kitchen', query: to.query }
  }

  if (isKitchenRoute) {
    const isDonePageOnly = !isKDSTerminal && !UserService.allows('access_terminals')

    if (!isKDSTerminal && !user) {
      return { name: 'userLogin' }
    }

    if (isDonePageOnly && to.name !== 'kitchenDoneTickets') {
      return { name: 'kitchenDoneTickets', query: to.query }
    }
  }
}

const retailMiddleware: MiddlewareFunction = async (to) => {
  const store = useAppStore()

  if(to.name === 'salesOrder' && store.isRetail) {
    return { name: 'sales' }
  }
}

const permissionCheckMiddleware: MiddlewareFunction = async (to) => {
  const store = useAppStore()
  const modules = store.modules

  const requiredPermissions = to.matched
    .map(route => route.meta.requiredPermission as string | undefined)
    .filter((p): p is string => !!p)

  if (!UserService.allows(requiredPermissions)) {
    return { name: 'userLogin' }
  }

  const enabledWithModules = to.matched
    .map(route => route.meta.enabledWithModules)
    .filter(p => !!p)
    .flat() as ModuleNameType[]

  if (enabledWithModules.length) {
    const modulesEnabled = enabledWithModules.map(mod => modules[mod])

    if (!modulesEnabled.some(Boolean)) {
      return false
    }
  }
}

// Main navigation guard
export function setupNavigationGuards(router: Router): void {
  router.beforeEach(async (to, from) => {
    const middlewares: MiddlewareFunction[] = [
      authMiddleware,
      gettingStartedMiddleware,
      kitchenRouteMiddleware,
      retailMiddleware,
      permissionCheckMiddleware
    ]

    for (const middleware of middlewares) {
      const result = await middleware(to)
      if (result !== undefined) {
        return result
      }
    }

    return true
  })
}
